import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField
} from '@mui/material'
import React, { FC, useCallback, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useCreateAdditionalEmailAddressMutation,
  useUpdateCustomerDetailMutation
} from '../../redux/api'
import { CustomerEntry } from '../../sdk'

interface Props {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  customer: CustomerEntry
}

export const AddEmailAddressDialog: FC<Props> = ({
  open,
  setOpen,
  customer
}) => {
  const { accessToken } = useAccessTokenAndShopId()

  const customerId = customer.id!
  const customerInformation = customer.information

  const [description, setDescription] = useState<string>('')
  const [emailAddress, setEmailAddress] = useState<string>('')

  const [createAdditionalEmailAddress] =
    useCreateAdditionalEmailAddressMutation()

  const createNewEmailAddress = useCallback(
    async (description: string, emailAddress: string) => {
      await createAdditionalEmailAddress({
        authorization: accessToken,
        customerAdditionalEmailAddressInfo: {
          customer: customerId,
          emailAddress,
          description
        }
      })
    },
    [accessToken, createAdditionalEmailAddress, customerId]
  )

  const [updateCustomer] = useUpdateCustomerDetailMutation()
  const markAsPrimary = useCallback(
    async (email: string) => {
      await updateCustomer({
        customerEntry: {
          id: customerId!,
          information: {
            ...customerInformation,
            email
          }
        },
        authorization: accessToken
      })
    },
    [updateCustomer, customerId, customerInformation, accessToken]
  )

  const handleSubmit = useCallback(async () => {
    if (!description || !emailAddress) {
      return
    }

    await createNewEmailAddress(description, emailAddress)
    await markAsPrimary(emailAddress)

    setDescription('')
    setEmailAddress('')
    setOpen(false)
  }, [description, emailAddress, createNewEmailAddress, markAsPrimary, setOpen])

  return (
    <Dialog open={open}>
      <DialogTitle>Add a new email address</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} pt={1}>
          <Grid item xs={6}>
            <TextField
              label={'Enter description'}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={'Enter email address'}
              value={emailAddress}
              onChange={(event) => setEmailAddress(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
